// Mitigación de "stale chunk" (ficha bugs/2026-07-19-cf-cache-html-assets-404):
// Cloudflare cachea el HTML anónimo de `/` y `/mastermind-pro`; tras un deploy
// ese HTML rancio referencia chunks con hash de un build viejo que puede no
// existir ya (evidencia real: MastermindProLanding-CEQ2PL07.js → 404) y el
// import dinámico falla dejando la página rota. La recuperación clásica es
// recargar la página UNA sola vez: la recarga pide el HTML de nuevo y, tras la
// purga del deploy, llega el fresco. El guard en sessionStorage evita un bucle
// de recargas si el HTML sigue rancio tras recargar: el segundo fallo se deja
// aflorar en vez de recargar otra vez.

export const STALE_CHUNK_RELOAD_KEY = "mm:stale-chunk-reload";

// Bug 111: al empezar una navegación de documento (enlace no Inertia, atrás
// fuera de la SPA, dirección tecleada) el navegador cancela los imports en
// vuelo (WebKit: "Load request cancelled"). Ese fallo NO es un chunk rancio y
// recargar pisaría la navegación que pidió el alumno. Por eso marcamos
// «saliendo» en beforeunload/pagehide y no recargamos mientras dure. La marca
// vive SOLO en memoria (muere con el documento) y caduca sola a los
// LEAVING_PAGE_TTL_MS: beforeunload también salta en navegaciones que al
// final no ocurren (p. ej. una descarga) y una marca pegada taparía un chunk
// rancio de verdad. pageshow (vuelta desde bfcache) también la limpia.
export const LEAVING_PAGE_TTL_MS = 3000;

let leavingPageUntil: number | null = null;

export function isLeavingPage(): boolean {
  if (leavingPageUntil === null) return false;
  if (Date.now() >= leavingPageUntil) {
    leavingPageUntil = null;
    return false;
  }
  return true;
}

export interface StaleChunkDeps {
  storage?: Storage;
  reload?: () => void;
}

// Devuelve true si ha lanzado la recarga (única) de recuperación; false si el
// guard ya estaba puesto o si no hay sessionStorage utilizable (sin guard no
// recargamos: un reload incondicional sí podría entrar en bucle).
export function reloadOnceForStaleChunk(deps: StaleChunkDeps = {}): boolean {
  const storage = deps.storage ?? window.sessionStorage;
  const reload = deps.reload ?? (() => window.location.reload());

  // El documento se está yendo: el fallo es la cancelación de la salida, no
  // un chunk rancio. Ni recargamos ni gastamos el guard.
  if (isLeavingPage()) return false;

  try {
    if (storage.getItem(STALE_CHUNK_RELOAD_KEY) !== null) return false;
    storage.setItem(STALE_CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    return false;
  }

  reload();
  return true;
}

// Se llama cuando una navegación carga su chunk con éxito: el auto-reload
// anterior (si lo hubo) funcionó, así que un futuro deploy puede volver a
// auto-recuperarse. Nunca se limpia entre un reload fallido y el siguiente
// fallo, porque sin navegación con éxito no hay señal de recuperación.
export function clearStaleChunkReloadGuard(
  storage: Storage = window.sessionStorage,
): void {
  try {
    storage.removeItem(STALE_CHUNK_RELOAD_KEY);
  } catch {
    // Sin storage no hay guard que limpiar.
  }
}

// Vite emite "vite:preloadError" en window cuando falla la carga de un import
// dinámico o de sus dependencias precargadas (caso típico: chunk de un build
// viejo). preventDefault() evita que Vite relance el error mientras la recarga
// está en vuelo; si el guard impide recargar, el error aflora con normalidad.
// Si el documento se está yendo (bug 111) también se silencia: la página se va
// y no hay nada que reportar.
export function installStaleChunkReload(
  target: Window = window,
  deps: StaleChunkDeps = {},
): void {
  // Sin preventDefault ni returnValue: jamás el diálogo «¿salir?».
  const markLeaving = () => {
    leavingPageUntil = Date.now() + LEAVING_PAGE_TTL_MS;
  };
  target.addEventListener("beforeunload", markLeaving);
  target.addEventListener("pagehide", markLeaving);
  target.addEventListener("pageshow", () => {
    leavingPageUntil = null;
  });

  target.addEventListener("vite:preloadError", (event) => {
    if (isLeavingPage() || reloadOnceForStaleChunk(deps)) {
      event.preventDefault();
    }
  });
}
